import React, { useRef, useState, useEffect } from 'react';
import { Modal, Button, FormGroup, ControlLabel, FormControl, Popover, OverlayTrigger, Collapse } from 'react-bootstrap';
import PropTypes from 'prop-types';

import Spinner from '../../../../../components/Loaders/Spinner';
import Alert from '../../../../../components/Alert';
import Tooltip from '../../../../../components/Tooltip';
import GroupWalls from '../../../../../components/GroupWalls';
import FormSwitch from '../../../../../components/FormSwitch';

import { API_KEY_LENGTH } from '../../helpers';

const EditModal = ({ open, item, onHide, onShow, error, isLoading, isLoadingSubmit, onSubmit, onDelete, onSendLog }) => {
  let popoverRef = useRef(null);
  const formNode = useRef(null);

  const [showLowBalance, setShowLowBalance] = useState(false);
  const [showAlias, setShowAlias] = useState(false);
  const [balanceLimit, setBalanceLimit] = useState('');
  const [showFields, setShowFields] = useState(false);

  const onChangeShowLowBalance = () => setShowLowBalance(!showLowBalance);
  const onChangeShowAlias = () => setShowAlias(!showAlias);

  const onSubmitForm = (event) => {
    event?.preventDefault();
    const data = new FormData(formNode.current);

    const payload = {};
    for (const name of data.keys()) {
      payload[name] = formNode.current[name].value;
    }
    if (payload?.event_enabled === 'on') {
      payload.event_enabled = true;
    }
    if (!payload?.alias_enabled) {
      payload.alias = '';
    }
    if (payload?.alias_enabled === 'on') {
      delete payload?.alias_enabled;
    }
    onSubmit?.(payload);
  };

  const onDeleteItem = () => {
    popoverRef?.handleHide();
    onDelete?.();
  };

  const onBlurLimit = (e) => {
    const newTarget = e;
    if (e.target.value) {
      newTarget.target.value = parseFloat(e.target.value);
      setBalanceLimit(newTarget.target.value);
    }
  };

  const onChangeLimit = (event) => {
    const value = event.target.value.replace(/[,,]/, '.');
    if (value === '' || value === '00') {
      setBalanceLimit('0');
    } else if (/^0[0-9].*$/.test(value)) {
      const newValue = value.replace(/^0+/, '');
      setBalanceLimit(newValue);
    } else {
      const newValue = value.replace(',', '.');
      if (/^[0-9]*(?:\.|\.[0-9]{0,9999})?$/.test(newValue)) {
        setBalanceLimit(newValue);
      }
    }
  };

  useEffect(() => {
    const filled = item?.authkeys_fields?.filter((field) => item?.[`${field?.id}_filled`]);
    return setShowFields(filled?.length === 0);
  }, [item, open]);

  useEffect(() => {
    const names = item?.authkeys_fields?.map((field) => field.id) || [];
    if (showFields) {
      names.forEach((name) => {
        if (formNode.current[name]) {
          formNode.current[name].value = '';
        }
      });
    } else {
      names.forEach((name) => {
        if (formNode.current[name]) {
          formNode.current[name].value = item?.[`${name}_filled`] ? '*'.repeat(API_KEY_LENGTH) : '';
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, showFields]);

  const popover = (
    <Popover id="popover-trigger-click-root-close" title="" className="text-center">
      <Button bsStyle="primary" bsSize="sm" onClick={onDeleteItem}>
        Delete
      </Button>
      <Button bsSize="sm" className="ml-8" onClick={() => popoverRef?.handleHide()}>
        Cancel
      </Button>
    </Popover>
  );

  return (
    <Modal
      show={open}
      onHide={() => {
        onHide();
      }}
      onExiting={() => {
        setShowFields(false);
        setShowLowBalance(false);
        setShowAlias(false);
      }}
      onShow={onShow}
      onEnter={() => {
        setBalanceLimit(item?.event_low_balance?.limit || '0');
        setShowLowBalance(!!item?.event_low_balance?.enable);
        setShowAlias(!!item?.alias);
      }}
      backdrop="static"
      bsSize="small"
      className="modal-md-custom"
    >
      {isLoading && <div className="modal-loader-small" />}
      <Modal.Header closeButton>
        <Modal.Title>Edit provider</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert text={error} />}

        <form onSubmit={onSubmitForm} ref={formNode}>
          <FormGroup>
            <ControlLabel>Provider</ControlLabel>
            <FormControl type="text" readOnly defaultValue={item?.name || ''} />
          </FormGroup>

          {item?.authkeys_fields?.map((field, index) => (
            <FormGroup key={field.id}>
              <ControlLabel htmlFor={field.id}>{field.title}</ControlLabel>
              <Tooltip
                placement="top"
                text={!showFields ? <span>The key is hidden for security reasons</span> : undefined}
                tooltipClass="tooltip__top"
              >
                <FormControl
                  type="text"
                  name={field.id}
                  id={field.id}
                  disabled={!showFields}
                  defaultValue={!showFields && item?.[`${field.id}_filled`] ? '*'.repeat(API_KEY_LENGTH) : ''}
                  autoFocus={index === 0}
                />
              </Tooltip>
            </FormGroup>
          ))}
          {!showFields ? (
            <FormGroup style={{ marginTop: '-7px' }}>
              <Button
                bsStyle="link"
                className="p-0 m-0"
                onClick={() => {
                  setShowFields(true);
                  onSendLog();
                }}
              >
                Change API key
              </Button>
            </FormGroup>
          ) : null}

          <FormGroup className="mb-0">
            <ControlLabel>Options</ControlLabel>
            <FormGroup className="mb-0">
              <GroupWalls.Wrapper>
                <GroupWalls.Item active={showAlias}>
                  <FormSwitch id="alias_enabled" name="alias_enabled" checked={showAlias} onChange={onChangeShowAlias}>
                    Alias name
                    {' '}
                    <Tooltip
                      placement="top"
                      text={<span>This name will be displayed instead of the provider's URL in the admin area interface for all admins</span>}
                      tooltipClass="tooltip__top"
                    >
                      <span className="fas fa-info-circle" />
                    </Tooltip>
                  </FormSwitch>
                  <Collapse in={showAlias}>
                    <div>
                      <FormGroup className="pt-12">
                        <FormControl type="text" name="alias" id="alias" defaultValue={item?.alias || ''} />
                      </FormGroup>
                    </div>
                  </Collapse>
                </GroupWalls.Item>
                {item?.event_low_balance ? (
                  <GroupWalls.Item active={showLowBalance}>
                    <FormSwitch id="event_enabled" name="event_enabled" checked={showLowBalance} onChange={onChangeShowLowBalance}>
                      Low balance notification
                      {' '}
                      <Tooltip
                        placement="top"
                        text={(
                          <span>
                            This notification will be displayed via the bell icon in your admin area when the provider balance is lower than the set
                            limit.
                          </span>
                        )}
                        tooltipClass="tooltip__top"
                      >
                        <span className="fas fa-info-circle" />
                      </Tooltip>
                    </FormSwitch>
                    <Collapse in={showLowBalance}>
                      <div>
                        <FormGroup className="pt-12">
                          <div className="input-group input-group__inline">
                            <div className="input__label-inside">
                              <ControlLabel className="top-label">
                                Notify if the provider’s balance is lower than,
                                {' '}
                                <span>{item?.provider_currency}</span>
                              </ControlLabel>
                              <FormControl
                                type="text"
                                name="balance_limit"
                                id="balance_limit"
                                placeholder="0"
                                value={balanceLimit}
                                onBlur={onBlurLimit}
                                onChange={onChangeLimit}
                              />
                            </div>
                          </div>
                        </FormGroup>
                      </div>
                    </Collapse>
                  </GroupWalls.Item>
                ) : null}
              </GroupWalls.Wrapper>
            </FormGroup>
          </FormGroup>
        </form>
      </Modal.Body>
      <Modal.Footer className="custom__modal-footer">
        <Button bsStyle="primary" type="submit" disabled={isLoadingSubmit} onClick={onSubmitForm}>
          {isLoadingSubmit && <Spinner />}
          <span>Save changes</span>
        </Button>
        <Button
          onClick={() => {
            onHide();
          }}
        >
          Cancel
        </Button>

        <div className="pull-right" style={{ position: 'relative' }}>
          <OverlayTrigger ref={(r) => (popoverRef = r)} trigger="click" rootClose placement="top" overlay={popover}>
            <Button bsStyle="link" className="modal-delete-btn">
              <span>Delete</span>
            </Button>
          </OverlayTrigger>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

EditModal.propTypes = {
  open: PropTypes.bool,
  item: PropTypes.object,
  isLoading: PropTypes.bool,
  isLoadingSubmit: PropTypes.bool,
  error: PropTypes.string,
  onHide: PropTypes.func,
  onShow: PropTypes.func,
  onSubmit: PropTypes.func,
  onDelete: PropTypes.func,
  onSendLog: PropTypes.func,
};

export default EditModal;
